import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import LucideIcon from "../../../components/LucideIcon";
import LoadingButton from "../../../components/LoadingButton";

interface ScheduleTerminationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  companyName: string;
  companyId: string;
  gracePeriodDays: number;
}

const ScheduleTerminationDialog: React.FC<ScheduleTerminationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  companyName,
  companyId,
  gracePeriodDays,
}) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset state whenever the dialog opens for a different company.
  useEffect(() => {
    if (open) {
      setReason("");
      setSubmitting(false);
    }
  }, [open, companyId]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Schedule company termination
        <IconButton onClick={onClose} disabled={submitting} size="small">
          <LucideIcon name="X" size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This schedules <strong>{companyName || companyId}</strong> for deletion in{" "}
          {gracePeriodDays} days, per the Data Management Policy. The company stays
          visible and reversible during the grace window; after that it is removed from
          the platform.
        </Alert>
        <Box>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            Reason (optional — stored for the compliance audit trail)
          </Typography>
          <TextField
            multiline
            minRows={3}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Contract terminated 2026-05-27 per support ticket #1234"
            disabled={submitting}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} disabled={submitting}>
          Cancel
        </LoadingButton>
        <LoadingButton
          loading={submitting}
          onClick={handleConfirm}
          variant="contained"
          color="error"
        >
          Schedule termination
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleTerminationDialog;
